import { useRef, useState } from "react";

import type { AutomationDraft } from "./automationFormModel";

type SubmissionState =
  | { readonly status: "idle"; readonly error: string | null }
  | { readonly status: "submitting"; readonly error: null }
  | {
      readonly status: "reconciliation-required";
      readonly error: string;
      readonly recoveryAutomationId: string | null;
    };

export type AutomationSubmissionResult =
  | string
  | null
  | {
      readonly error: string;
      readonly reconciliationRequired: true;
      readonly recoveryAutomationId?: string;
    };

export function useAutomationFormSubmission(input: {
  readonly draft: AutomationDraft;
  readonly canSubmit: boolean;
  readonly onSubmit: (draft: AutomationDraft) => Promise<AutomationSubmissionResult>;
}) {
  const active = useRef(false);
  const reconciliationRequired = useRef(false);
  const [state, setState] = useState<SubmissionState>({ status: "idle", error: null });

  function clearError(): void {
    if (state.status === "idle" && state.error !== null) {
      setState({ status: "idle", error: null });
    }
  }

  async function submit(): Promise<void> {
    if (!input.canSubmit || active.current || reconciliationRequired.current) return;
    active.current = true;
    setState({ status: "submitting", error: null });
    try {
      const result = await input.onSubmit(input.draft);
      if (typeof result === "object" && result !== null) {
        reconciliationRequired.current = true;
        setState({
          status: "reconciliation-required",
          error: result.error,
          recoveryAutomationId: result.recoveryAutomationId ?? null,
        });
      } else {
        setState({ status: "idle", error: result });
      }
    } catch {
      setState({
        status: "idle",
        error: "The automation could not be saved. Check your connection and try again.",
      });
    } finally {
      active.current = false;
    }
  }

  return {
    clearError,
    error: state.error,
    reconciliationRequired: state.status === "reconciliation-required",
    recoveryAutomationId:
      state.status === "reconciliation-required" ? state.recoveryAutomationId : null,
    submitting: state.status === "submitting",
    submit,
  };
}
